/**
 * Shared UI components — index barrel export
 *
 * StatCard, Badge, Toggle, Modal, Pagination, DataTable
 */

// ─── StatCard ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number | string;
  sub?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon?: string;
}

export function StatCard({ label, value, sub, trend, trendValue, icon }: StatCardProps) {
  return (
    <div className="stat-card">
      {icon && (
        <span style={{
          display: 'block',
          fontSize: 16,
          color: 'var(--accent-light)',
          marginBottom: 10,
          fontFamily: 'monospace',
          fontWeight: 700,
        }}>
          {icon}
        </span>
      )}
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">
        {typeof value === 'number' ? value.toLocaleString('vi-VN') : value}
      </div>
      {(sub || trendValue) && (
        <div className="stat-card__sub">
          {trendValue && (
            <span
              className={
                trend === 'up'
                  ? 'stat-card__trend-up'
                  : trend === 'down'
                  ? 'stat-card__trend-down'
                  : ''
              }
            >
              {trend === 'up' ? '↑' : trend === 'down' ? '↓' : ''} {trendValue}{' '}
            </span>
          )}
          {sub}
        </div>
      )}
    </div>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────

type BadgeVariant =
  | 'free'
  | 'basic'
  | 'premium'
  | 'member'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

interface BadgeProps {
  variant: BadgeVariant;
  children: React.ReactNode;
}

const TIER_LABELS: Record<string, string> = {
  free: 'Free',
  basic: 'Basic',        // legacy - giữ lại để hiển thị data cũ
  member: 'Member',
  premium: 'Premium',
};

export function Badge({ variant, children }: BadgeProps) {
  return <span className={`badge badge-${variant}`}>{children}</span>;
}

export function TierBadge({ tier }: { tier: string }) {
  const variant =
    tier === 'premium' ? 'premium' : tier === 'member' || tier === 'basic' ? 'basic' : 'free';
  return (
    <Badge variant={variant as BadgeVariant}>
      {TIER_LABELS[tier] ?? tier}
    </Badge>
  );
}

export function AccessTierBadge({ tier }: { tier: string }) {
  const map: Record<string, BadgeVariant> = {
    free: 'free',
    member: 'member',
    premium: 'premium',
  };
  const labels: Record<string, string> = {
    free: '🔓 Free',
    member: '👤 Member',
    premium: '⭐ Premium',
  };
  return (
    <Badge variant={map[tier] ?? 'info'}>
      {labels[tier] ?? tier}
    </Badge>
  );
}

// ─── Toggle ───────────────────────────────────────────────────────────────────

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
}

export function Toggle({ checked, onChange, label, disabled }: ToggleProps) {
  const id = `toggle-${Math.random().toString(36).slice(2)}`;
  return (
    <label className="toggle" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        className="toggle__input"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="toggle__track" />
      {label && <span style={{ color: 'var(--text-2)', fontSize: 13 }}>{label}</span>}
    </label>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

export function Modal({ isOpen, onClose, title, children, actions }: ModalProps) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal__title">{title}</h3>
        <div className="modal__body">{children}</div>
        {actions && <div className="modal__actions">{actions}</div>}
      </div>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

interface PaginationProps {
  page: number;
  totalPages: number;
  totalRows: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  hasPrev: boolean;
  hasNext: boolean;
}

export function Pagination({
  page,
  totalPages,
  totalRows,
  pageSize,
  onPageChange,
  hasPrev,
  hasNext,
}: PaginationProps) {
  const start = Math.min((page - 1) * pageSize + 1, totalRows);
  const end = Math.min(page * pageSize, totalRows);

  // Show at most 5 page buttons
  const pages: number[] = [];
  const half = 2;
  let from = Math.max(1, page - half);
  let to = Math.min(totalPages, page + half);
  if (to - from < 4) {
    if (from === 1) to = Math.min(totalPages, from + 4);
    else from = Math.max(1, to - 4);
  }
  for (let i = from; i <= to; i++) pages.push(i);

  if (totalPages <= 1) return null;

  return (
    <div className="pagination">
      <span className="pagination__info">
        {start}–{end} / {totalRows.toLocaleString('vi-VN')} kết quả
      </span>
      <div className="pagination__buttons">
        <button
          className="pagination__btn"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
        >
          ‹
        </button>
        {from > 1 && (
          <>
            <button className="pagination__btn" onClick={() => onPageChange(1)}>1</button>
            {from > 2 && <span style={{ color: 'var(--text-3)', fontSize: 12, padding: '0 4px' }}>…</span>}
          </>
        )}
        {pages.map((p) => (
          <button
            key={p}
            className={`pagination__btn ${p === page ? 'active' : ''}`}
            onClick={() => onPageChange(p)}
          >
            {p}
          </button>
        ))}
        {to < totalPages && (
          <>
            {to < totalPages - 1 && (
              <span style={{ color: 'var(--text-3)', fontSize: 12, padding: '0 4px' }}>…</span>
            )}
            <button className="pagination__btn" onClick={() => onPageChange(totalPages)}>
              {totalPages}
            </button>
          </>
        )}
        <button
          className="pagination__btn"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
        >
          ›
        </button>
      </div>
    </div>
  );
}

// ─── DataTable ────────────────────────────────────────────────────────────────

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  render?: (row: T) => React.ReactNode;
}

interface DataTableProps<T extends Record<string, unknown>> {
  columns: Column<T>[];
  rows: T[];
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  emptyText?: string;
  // From useTable hook
  sortKey?: string | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
}

export function DataTable<T extends Record<string, unknown>>({
  columns,
  rows,
  isLoading,
  onRowClick,
  emptyText = 'Không có dữ liệu',
  sortKey,
  sortDir,
  onSort,
}: DataTableProps<T>) {
  if (isLoading) {
    return (
      <div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="skeleton skeleton-row" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state__icon">📭</div>
        <div className="empty-state__text">{emptyText}</div>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table>
        <thead>
          <tr>
            {columns.map((col) => {
              const isActive = sortKey === col.key;
              return (
                <th
                  key={col.key}
                  className={`${col.sortable ? 'sortable' : ''} ${isActive ? 'sort-active' : ''}`}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={col.sortable && onSort ? () => onSort(col.key) : undefined}
                >
                  {col.label}
                  {col.sortable && isActive && (
                    <span className="sort-icon">{sortDir === 'asc' ? '↑' : '↓'}</span>
                  )}
                  {col.sortable && !isActive && (
                    <span className="sort-icon" style={{ opacity: 0.3 }}>↕</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} onClick={() => onRowClick?.(row)}>
              {columns.map((col) => (
                <td key={col.key}>
                  {col.render
                    ? col.render(row)
                    : (row[col.key] as React.ReactNode) ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';

interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}

let _addToast: ((message: string, type: ToastItem['type']) => void) | null = null;

export function toast(message: string, type: ToastItem['type'] = 'info') {
  _addToast?.(message, type);
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    _addToast = (message, type) => {
      const id = Date.now().toString();
      setToasts((prev) => [...prev, { id, message, type }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 3500);
    };
    return () => { _addToast = null; };
  }, []);

  const icons = { success: '✅', error: '❌', warning: '⚠️', info: 'ℹ️' };

  return (
    <div className="toast-container">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          <span>{icons[t.type]}</span>
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Format helpers ───────────────────────────────────────────────────────────

export function formatDate(unixepoch: number): string {
  return new Date(unixepoch * 1000).toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatDateTime(unixepoch: number): string {
  return new Date(unixepoch * 1000).toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatVND(amount: number): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function timeAgo(unixepoch: number): string {
  const diff = Math.floor(Date.now() / 1000 - unixepoch);
  if (diff < 60) return 'vừa xong';
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)} ngày trước`;
  return formatDate(unixepoch);
}

// ─── SearchInput ─────────────────────────────────────────────────────────────

interface SearchInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  id?: string;
}

export function SearchInput({ value, onChange, placeholder = 'Tìm kiếm...', id }: SearchInputProps) {
  return (
    <div className="search-input-wrap">
      <span className="search-input-icon">🔍</span>
      <input
        id={id}
        type="search"
        className="search-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {value && (
        <button className="search-input-clear" onClick={() => onChange('')} title="Xóa">
          ✕
        </button>
      )}
    </div>
  );
}

// ─── LoadingSpinner ───────────────────────────────────────────────────────────

export function LoadingSpinner({ size = 32 }: { size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        border: '3px solid var(--border)',
        borderTopColor: 'var(--accent)',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }}
    />
  );
}

// ─── ErrorBanner ─────────────────────────────────────────────────────────────

export function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      style={{
        background: 'rgba(239,68,68,0.1)',
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: 8,
        padding: '12px 16px',
        color: '#f87171',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <span>⚠️</span>
      <span style={{ flex: 1 }}>{message}</span>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            background: 'transparent',
            border: '1px solid #f87171',
            color: '#f87171',
            borderRadius: 6,
            padding: '4px 10px',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          Thử lại
        </button>
      )}
    </div>
  );
}
